// src/App.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { FaChartBar, FaShoppingCart, FaMoneyBillWave, FaLink, FaTrophy, FaExchangeAlt, FaCreditCard } from 'react-icons/fa';
import Header from './components/HeaderComponents/Header';
import MobileNav from './components/MobileNav';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Marketplace from './components/Marketplace';
import CreateOrder from './components/CreateOrder';
import TraderProfile from './components/TraderProfile/TraderProfile';
import TraderCard from './components/TraderCard';
import MyTrades from './components/MyTrades/MyTrades';
import TradeDetail from './components/TradeDetail/TradeDetail';
import TradeConfirm from './components/TradeConfirm/TradeConfirm';
import UserProfile from './components/UserProfile/UserProfile';
import MyListings from './components/MyListings/MyListings';
import GameRewards from './components/GameRewards/GameRewards';
import ArkanaGuide from './components/ArkanaGuide/ArkanaGuide';
import Portfolio from './components/Portfolio/Portfolio';
import SecurityCenter from './components/Pages/SecurityCenter';
import AmlKyc from './components/Pages/AmlKyc';
import Faq from './components/Pages/Faq';
import Videos from './components/Pages/Videos';
import OneSignalInit from './components/OneSignalInit';
import CityFX from './components/CityFX/CityFX';
import Login from './components/Login Components/Login.jsx';
import Register from './components/Register Components/Register.jsx';
import ForgotPassword from './components/AuthPages/ForgotPassword';
import ResetPassword from './components/AuthPages/ResetPassword';
import AdminApp from './components/Admin/AdminApp';
import { AuthGateProvider } from './Context/AuthGateContext';
import { ToastProvider } from './Context/ToastContext';

import offersData from './data/mockOffers.json';
import './App.css';

const API_URL          = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const TRADERS_PER_PAGE = 10;

function ProtectedRoute({ children }) {
  const token    = localStorage.getItem('token');
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// Link vanity del storefront: solo /@usuario renderiza perfil; cualquier otro segmento → home
function VanityProfile({ offers }) {
  const { trader } = useParams();
  if (!trader || !trader.startsWith('@')) return <Navigate to="/" replace />;
  return <TraderProfile offers={offers} />;
}

function AppLayout({ collapsed, toggleMenu, children }) {
  return (
    <div className={`app-container ${collapsed ? 'sidebar-collapsed' : 'sidebar-open'}`}>
      <CityFX />
      <Sidebar collapsed={collapsed} toggleMenu={toggleMenu} />
      <div className="app-content">
        <Header toggleMenu={toggleMenu} />
        <main className="main-content">{children}</main>
        <Footer />
      </div>
      <MobileNav />
      <ArkanaGuide />
      <OneSignalInit />
    </div>
  );
}

function AnimatedNumber({ value, prefix = '' }) {
  const [display, setDisplay] = useState(value);
  const [flash, setFlash] = useState(false);
  const prevRef = useRef(value);

  useEffect(() => {
    if (value !== prevRef.current) {
      setFlash(true);
      // Animate count
      const start = prevRef.current;
      const end = value;
      const duration = 600;
      const startTime = Date.now();
      const tick = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setDisplay(Math.round(start + (end - start) * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
      prevRef.current = value;
      setTimeout(() => setFlash(false), 600);
    }
  }, [value]);

  const formatted = typeof display === 'number' && display > 1000
    ? `${prefix}${(display / 1000).toFixed(0)}K`
    : `${prefix}${display.toLocaleString()}`;

  return <span className={`market-stat-val ${flash ? 'msv-flash' : ''}`}>{formatted}</span>;
}

function MarketStatsWidget({ offers }) {
  const total        = offers.length;
  const buys         = offers.filter(o => o.type === 'buy').length;
  const sells        = offers.filter(o => o.type === 'sell').length;
  const totalVol     = offers.reduce((s, o) => s + Number(o.amount || 0), 0);
  const uniqueChains = [...new Set(offers.map(o => o.network || o.blockchain).filter(Boolean))].length;
  const buyPct       = total > 0 ? Math.round((buys / total) * 100) : 0;
  const sellPct      = total > 0 ? Math.round((sells / total) * 100) : 0;

  // Precio de mercado (USDT ≈ $1 + spread). Desde la óptica del usuario:
  // comprar USDT = ofertas de VENTA del anunciante (precio más bajo conviene);
  // vender USDT = ofertas de COMPRA del anunciante (precio más alto conviene).
  const priceOf    = o => 1 + (Number(o.spread) || 0) / 100;
  const sellPrices = offers.filter(o => o.type === 'sell').map(priceOf);
  const buyPrices  = offers.filter(o => o.type === 'buy').map(priceOf);
  const allPrices  = offers.map(priceOf);
  const bestBuy    = sellPrices.length ? Math.min(...sellPrices) : null;  // comprar USDT desde
  const bestSell   = buyPrices.length  ? Math.max(...buyPrices)  : null;  // vender USDT hasta
  const avgPrice   = allPrices.length  ? allPrices.reduce((a, b) => a + b, 0) / allPrices.length : null;
  const px = p => p == null ? '—' : `$${p.toFixed(3)}`;

  // Métodos de pago más ofrecidos (los datos usan `payment` o `paymentMethod`)
  const payCount = {};
  offers.forEach(o => String(o.payment ?? o.paymentMethod ?? '').split(',').map(s => s.trim()).filter(Boolean)
    .forEach(p => { payCount[p] = (payCount[p] || 0) + 1; }));
  const topPays = Object.entries(payCount).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <div className="market-stats-widget msw-live">
      <h3 className="widget-title"><FaChartBar style={{marginRight: '8px'}} /> Mercado en vivo <span className="msw-live-dot" /></h3>
      <div className="market-stats-grid">
        <div className="market-stat"><AnimatedNumber value={total} /><span className="market-stat-label">Ofertas activas</span></div>
        <div className="market-stat buy"><AnimatedNumber value={buys} /><span className="market-stat-label"><FaShoppingCart style={{marginRight: '4px'}} /> Compras</span></div>
        <div className="market-stat sell"><AnimatedNumber value={sells} /><span className="market-stat-label"><FaMoneyBillWave style={{marginRight: '4px'}} /> Ventas</span></div>
        <div className="market-stat" title="Suma de la liquidez publicada en las ofertas activas (no es volumen ya operado)."><AnimatedNumber value={totalVol} prefix="$" /><span className="market-stat-label">Liquidez activa</span></div>
      </div>

      {/* Precio de mercado — lo que un trader necesita saber al instante */}
      <div className="market-price">
        <div className="market-price-head"><FaExchangeAlt style={{marginRight: '6px'}} /> Precio del mercado <span className="market-price-unit">USDT</span></div>
        <div className="market-price-rows">
          <div className="market-price-cell"><span className="mpc-k"><FaShoppingCart /> Comprar desde</span><strong className="fin-num mpc-buy">{px(bestBuy)}</strong></div>
          <div className="market-price-cell"><span className="mpc-k"><FaMoneyBillWave /> Vender hasta</span><strong className="fin-num mpc-sell">{px(bestSell)}</strong></div>
          <div className="market-price-cell"><span className="mpc-k">Promedio</span><strong className="fin-num">{px(avgPrice)}</strong></div>
        </div>
      </div>

      <div className="market-chains">
        <span className="market-chains-label"><FaLink style={{marginRight: '4px'}} /> {uniqueChains} redes activas</span>
        <div className="market-buy-bar">
          <div className="market-buy-fill" style={{ width: `${buyPct}%`, transition: 'width 0.8s ease' }} />
        </div>
        <div className="market-bar-labels">
          <span style={{ color: '#0091ea' }}><FaShoppingCart style={{marginRight: '4px'}} /> {buyPct}%</span>
          <span style={{ color: '#00b248' }}><FaMoneyBillWave style={{marginRight: '4px'}} /> {sellPct}%</span>
        </div>
      </div>

      {/* Métodos de pago más ofrecidos */}
      {topPays.length > 0 && (
        <div className="market-pays">
          <span className="market-pays-label"><FaCreditCard style={{marginRight: '4px'}} /> Pagos más usados</span>
          <div className="market-pays-chips">
            {topPays.map(([name, n]) => (
              <span key={name} className="market-pay-chip" title={`${n} ofertas`}>{name} <span className="market-pay-n">{n}</span></span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Deep-link de la landing: /marketplace?... → / conservando el query string
function MarketplaceRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}

function App() {
  const [collapsed, setCollapsed]   = useState(true);
  const [offers, setOffers]         = useState([]);
  const [apiOffers, setApiOffers]   = useState([]);
  const [traderPage, setTraderPage] = useState(1);
  const [railCollapsed, setRailCollapsed] = useState(() => localStorage.getItem('bitplify_railCollapsed') === '1');
  const toggleRail = (c) => { setRailCollapsed(c); localStorage.setItem('bitplify_railCollapsed', c ? '1' : '0'); };

  useEffect(() => {
    const fetchApiOffers = async () => {
      try {
        const res  = await fetch(`${API_URL}/api/offers`);
        if (!res.ok) return;
        const data = await res.json();
        setApiOffers(data.map(o => ({
          id:           `api-${o.id}`,
          trader:       o.trader      ?? o.user?.username ?? 'Trader',
          type:         o.type        ?? 'sell',
          currency:     o.currency    ?? 'USDT',
          country:      o.country && o.country !== 'all' ? o.country : (o.user?.country ?? 'all'),
          amount:       parseFloat(o.amount)       || 0,
          minAmount:    parseFloat(o.minAmount)    || 0,
          blockchain:   o.network     ?? o.blockchain ?? 'Bitcoin',
          payment:      o.payment     ?? 'Transferencia bancaria',
          responseTime: parseInt(o.responseTime)   || 5,
          spread:       parseFloat(o.spread)       || 0,
          rating:       parseFloat(o.rating)       || 4.0,
          trades:       parseInt(o.trades)         || 0,
          viewers:      0,
          sellerId:     o.userId,
        })));
      } catch {}
    };
    fetchApiOffers();
    const interval = setInterval(fetchApiOffers, 30000);
    return () => clearInterval(interval);
  }, []);

  const allOffers = useMemo(() => [...apiOffers, ...offersData, ...offers], [apiOffers, offers]);

  const handleToggleSidebar = () => setCollapsed(prev => !prev);

  const handleCreateOffer = newOffer => {
    setOffers(prev => [...prev, {
      ...newOffer,
      id:           `offer${Date.now()}`,
      currency:     newOffer.currency     ?? 'USDT',
      volume:       0,
      amount:       parseFloat(newOffer.amount)     || 0,
      spread:       parseFloat(newOffer.spread)     || 0,
      rating:       parseFloat(newOffer.rating)     || 4.0,
      trades:       parseInt(newOffer.trades)       || 0,
      viewers:      0,
      responseTime: parseInt(newOffer.responseTime) || 5,
      blockchain:   newOffer.network ?? newOffer.blockchain ?? 'Bitcoin',
      payment:      newOffer.paymentMethod ?? newOffer.payment ?? 'Transferencia bancaria',
    }]);
  };

  const [traderSort, setTraderSort] = useState('volume');
  const rankedTraders = useMemo(() => {
    return Array.from(new Set(allOffers.map(o => o.trader)))
      .map(name => {
        const tOffers = allOffers.filter(o => o.trader === name);
        return {
          name,
          volume:   tOffers.reduce((sum, o) => sum + Number(o.amount || 0), 0),
          rating:   tOffers.reduce((m, o) => Math.max(m, parseFloat(o.rating) || 0), 0),
          activity: tOffers.length,
        };
      })
      .sort((a, b) => traderSort === 'rating' ? b.rating - a.rating
                    : traderSort === 'activity' ? b.activity - a.activity
                    : b.volume - a.volume);
  }, [allOffers, traderSort]);

  const uniqueTraders    = rankedTraders.map(t => t.name);
  const totalTraderPages = Math.ceil(uniqueTraders.length / TRADERS_PER_PAGE);
  const paginatedTraders = uniqueTraders.slice((traderPage - 1) * TRADERS_PER_PAGE, traderPage * TRADERS_PER_PAGE);

  // Vista del home (P2P / Precios / Muro): mismo layout, el Marketplace elige el tab por la URL.
  // Es una función (no una const) para que cada ruta reciba un elemento DISTINTO; si compartieran
  // el mismo objeto, React omitiría el re-render al cambiar de /→/muro y el tab no se actualizaría.
  const makeHome = () => (
    <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
      <div className="main-layout">
        <div className="left-panel expanded-panel">
          <Marketplace externalOffers={[...offersData, ...offers]} />
        </div>
        <div className={`right-panel ${railCollapsed ? 'rail-collapsed' : ''}`}>
          <button className="rail-toggle" onClick={() => toggleRail(!railCollapsed)} title={railCollapsed ? 'Mostrar panel lateral' : 'Colapsar panel lateral'} aria-label="Colapsar panel">{railCollapsed ? '«' : '»'}</button>
          <MarketStatsWidget offers={allOffers} />
          <div className="leaderboard-section">
            <div className="leaderboard-header">
              <h3 className="leaderboard-title"><FaTrophy style={{marginRight: '8px'}} /> Top Traders</h3>
              <div className="leaderboard-sort">
                {[{ k: 'volume', l: 'Liquidez' }, { k: 'rating', l: 'Rating' }, { k: 'activity', l: 'Actividad' }].map(s => (
                  <button key={s.k} className={`lb-sort-btn ${traderSort === s.k ? 'active' : ''}`} onClick={() => { setTraderSort(s.k); setTraderPage(1); }}>{s.l}</button>
                ))}
              </div>
            </div>
            <div className="trader-list">
              {paginatedTraders.map((name, i) => {
                const globalRank = (traderPage - 1) * TRADERS_PER_PAGE + i + 1;
                return (
                  <Link key={name} to={`/profile/${name}`} style={{ textDecoration: 'none' }} className={i >= 3 ? 'trader-hide-mobile' : ''}>
                    <TraderCard trader={name} offers={allOffers} rank={globalRank} />
                  </Link>
                );
              })}
            </div>
            {totalTraderPages > 1 && (
              <div className="trader-pagination">
                <button className="trader-page-btn" onClick={() => setTraderPage(p => Math.max(p-1,1))} disabled={traderPage===1}>← Ant</button>
                <span className="trader-page-info">{traderPage} / {totalTraderPages}</span>
                <button className="trader-page-btn" onClick={() => setTraderPage(p => Math.min(p+1,totalTraderPages))} disabled={traderPage===totalTraderPages}>Sig →</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );

  return (
    <ToastProvider>
    <AuthGateProvider>
    <Router>
      <Routes>
        <Route path="/login"    element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot"         element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Panel admin interno (empleados) — módulo autocontenido, fuera del AppLayout */}
        <Route path="/admin/*"  element={<AdminApp />} />

        {/* Home P2P + sub-secciones con URL propia (el Marketplace elige el tab por la ruta) */}
        <Route path="/"        element={makeHome()} />
        <Route path="/precios" element={makeHome()} />
        <Route path="/muro"    element={makeHome()} />

        <Route path="/create" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <CreateOrder onCreate={handleCreateOffer} />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/trade/confirm" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <TradeConfirm />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/trades" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <MyTrades />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/trades/:id" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <TradeDetail />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/my-listings" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <MyListings />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <UserProfile />
            </AppLayout>
          </ProtectedRoute>
        } />

        <Route path="/profile/:trader" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <TraderProfile offers={allOffers} />
            </AppLayout>
        } />

        {/* Link vanity del storefront: bitplify.io/@usuario (resuelve por slug o username) */}
        <Route path="/:trader" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <VanityProfile offers={allOffers} />
            </AppLayout>
        } />

        <Route path="/rewards" element={
          <ProtectedRoute>
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <GameRewards />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Portafolio abierto a invitados: muestra demo + CTAs para registrarse */}
        <Route path="/portfolio" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <Portfolio />
            </AppLayout>
        } />

        {/* Páginas informativas — públicas (footer) */}
        <Route path="/security" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <SecurityCenter />
            </AppLayout>
        } />
        <Route path="/aml" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <AmlKyc />
            </AppLayout>
        } />
        <Route path="/faq" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <Faq />
            </AppLayout>
        } />
        <Route path="/videos" element={
            <AppLayout collapsed={collapsed} toggleMenu={handleToggleSidebar}>
              <Videos />
            </AppLayout>
        } />

        <Route path="/marketplace" element={<MarketplaceRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </AuthGateProvider>
    </ToastProvider>
  );
}

export default App;
